// Management screen for platform companies, reached from the top-level app
// menu. Companies govern tournaments: every tournament belongs to exactly one,
// and management creates a company here before its tournaments can be created.
// The list can be narrowed by name. The default company (the target of the
// pre-company migration) can be renamed but never deleted, and a company that
// still governs tournaments cannot be deleted either — the server enforces
// both rules; this screen surfaces its errors.

import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedIcon from '@mui/icons-material/Verified';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import type { CompanyType } from '../domain';
import {
  COMPANY_NAME_MAX,
  COMPANY_TYPES,
  DEFAULT_COMPANY_ID,
  DEFAULT_COMPANY_TYPE,
  findSeatUsage,
  isCompanyVerified,
  localizedLocationLabel,
  locationMatchesQuery,
  sortLocationsByName,
} from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';

interface CompaniesViewProps {
  /** Open a company's profile page. */
  onOpen: (companyId: number) => void;
}

export function CompaniesView({ onOpen }: CompaniesViewProps) {
  const {
    companies,
    companySeats,
    locations,
    summaries,
    createCompany,
    removeCompany,
    verifyCompany,
  } = useTournament();
  const { hasPermission } = useAuth();
  const { t, locale } = useTranslation();
  // Creating and deleting companies are platform-level company management;
  // without it the screen is a read-only list of accessible ones. Renaming
  // and profile edits happen on the company's own profile page, opened from
  // here.
  const canManage = hasPermission('CREATE_COMPANY');
  // Verifying a self-registered company is the platform's review action
  // (Super-Admin territory; see `features/company-registration.md`).
  const canVerify = hasPermission('VERIFY_COMPANY');

  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // New-company dialog: name, type (only Sport Club for now) and home location
  // (a dictionary entry) — the last two are fixed at creation and immutable.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<CompanyType>(DEFAULT_COMPANY_TYPE);
  const [newLocationId, setNewLocationId] = useState<number | ''>('');

  // How many tournaments each company governs, to explain why deletion is
  // blocked without a server round-trip.
  const ownedCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const summary of summaries) {
      counts.set(summary.companyId, (counts.get(summary.companyId) ?? 0) + 1);
    }
    return counts;
  }, [summaries]);

  // The picker lists cities under the names it displays, so it sorts by those
  // rather than by the canonical English order the server returns.
  const sortedLocations = useMemo(
    () => sortLocationsByName(locations, locale),
    [locations, locale],
  );

  const visible = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return companies;
    return companies.filter((company) =>
      company.name.toLowerCase().includes(query),
    );
  }, [companies, filter]);

  const runAction = async (action: () => Promise<void>) => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
    } catch (e: unknown) {
      setActionError(
        e instanceof Error ? e.message : t('admin.common.requestFailed'),
      );
    } finally {
      setBusy(false);
    }
  };

  const openCreate = () => {
    setNewName('');
    setNewType(DEFAULT_COMPANY_TYPE);
    // No preselect: the dictionary is far too long for its first entry to be a
    // sensible default, and the location can never be changed afterwards. The
    // create button stays disabled until one is picked.
    setNewLocationId('');
    setActionError(null);
    setDialogOpen(true);
  };

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name || newLocationId === '' || busy) return;
    await runAction(async () => {
      await createCompany(name, newType, newLocationId);
      setDialogOpen(false);
    });
  };

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t('admin.companies.title')}
        </Typography>
        <TextField
          size="small"
          label={t('admin.companies.filterByName')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: { xs: '100%', sm: 260 } }}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.companies.intro')}
      </Typography>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      {visible.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('admin.companies.emptyFiltered')}
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <List dense disablePadding>
            {visible.map((company) => {
              const owned = ownedCounts.get(company.id) ?? 0;
              // Seats are the subscription-plan figure (see
              // `features/subscription-seats.md`): the total sits in the row so a
              // Super Admin can scan every club's plan size at once, with the
              // breakdown behind its tooltip and on the company's own page.
              const seats = findSeatUsage(companySeats, company.id);
              const isDefault = company.id === DEFAULT_COMPANY_ID;
              const verified = isCompanyVerified(company);
              return (
                <ListItem
                  key={company.id}
                  divider
                  disablePadding
                  secondaryAction={
                    !canManage && !(canVerify && !verified) ? undefined : (
                      <Stack direction="row">
                        {canVerify && !verified && (
                          <Tooltip title={t('admin.companies.verify')}>
                            <IconButton
                              aria-label={t('admin.companies.verifyAria', {
                                name: company.name,
                              })}
                              disabled={busy}
                              onClick={() =>
                                void runAction(() => verifyCompany(company.id))
                              }
                            >
                              <VerifiedOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canManage && (
                          <>
                            <Tooltip title={t('admin.companies.openProfile')}>
                              <IconButton
                                aria-label={t('admin.companies.editAria', {
                                  name: company.name,
                                })}
                                disabled={busy}
                                onClick={() => onOpen(company.id)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                isDefault
                                  ? t('admin.companies.defaultCannotDelete')
                                  : owned > 0
                                    ? t('admin.companies.governs', { count: owned })
                                    : ''
                              }
                            >
                              <span>
                                <IconButton
                                  aria-label={t('admin.companies.deleteAria', {
                                    name: company.name,
                                  })}
                                  disabled={busy || isDefault || owned > 0}
                                  onClick={() =>
                                    void runAction(() => removeCompany(company.id))
                                  }
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    )
                  }
                >
                  <ListItemButton
                    onClick={() => onOpen(company.id)}
                    sx={{ pr: canManage || canVerify ? 14 : 2 }}
                  >
                    <ListItemText
                      primary={
                        // Every row carries its verification state: a green
                        // mark once the platform's review is done, the orange
                        // pending pill while a self-registered club awaits it
                        // (see `features/company-registration.md`).
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography component="span">{company.name}</Typography>
                          <Tooltip
                            title={t('admin.seats.breakdown', {
                              athletes: seats.athletes,
                              coaches: seats.coaches,
                            })}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t('admin.seats.seatCount', {
                                count: seats.billableSeats,
                              })}
                            />
                          </Tooltip>
                          {verified ? (
                            <Tooltip title={t('admin.companies.verifiedMark')}>
                              <VerifiedIcon
                                color="success"
                                sx={{ fontSize: 18 }}
                                titleAccess={t('admin.companies.verifiedMark')}
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={t('admin.companies.unverified')}
                            />
                          )}
                        </Stack>
                      }
                      secondary={t('admin.companies.tournamentCount', {
                        count: owned,
                      })}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}

      {canManage && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={busy}
            onClick={openCreate}
          >
            {t('admin.companies.newCompany')}
          </Button>
        </Stack>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('admin.companies.newCompany')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('admin.companies.dialog.name')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitCreate();
              }}
              autoFocus
              fullWidth
              slotProps={{ htmlInput: { maxLength: COMPANY_NAME_MAX } }}
            />
            <TextField
              select
              label={t('admin.companies.type')}
              value={newType}
              onChange={(e) => setNewType(e.target.value as CompanyType)}
              fullWidth
            >
              {COMPANY_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`admin.companies.types.${type}`)}
                </MenuItem>
              ))}
            </TextField>
            {locations.length === 0 ? (
              <Alert severity="info">
                {t('admin.companies.dialog.noLocations')}
              </Alert>
            ) : (
              <Autocomplete
                // Enter picks the best match, as in the sign-up wizard.
                autoHighlight
                options={sortedLocations}
                value={
                  locations.find((l) => l.id === newLocationId) ?? null
                }
                onChange={(_e, location) =>
                  setNewLocationId(location?.id ?? '')
                }
                getOptionLabel={(location) =>
                  localizedLocationLabel(location, locale)
                }
                // `locations` is a fresh array after every refresh, so options
                // are never reference-equal to the selected value.
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(options, { inputValue }) =>
                  options.filter((location) =>
                    locationMatchesQuery(location, inputValue, locale),
                  )
                }
                noOptionsText={t('admin.companies.dialog.noLocationMatches')}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('admin.companies.location')}
                    placeholder={t(
                      'admin.companies.dialog.locationPlaceholder',
                    )}
                    helperText={t('admin.companies.dialog.locationHelp')}
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={busy}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitCreate()}
            disabled={busy || newName.trim().length === 0 || newLocationId === ''}
          >
            {t('admin.companies.dialog.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
